import { User } from '@choco-admin/types';
import { useCallback } from 'react';

import { useUsersPageGetUserQuery } from '../../../generated';
import { mapToUser } from '../utils/userMapper';

export type CaptureExceptionFunction = (error: Error) => void;

export type UseGetUserResponse = {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

export type UseGetUserConfig = {
  userId: string;
  skip?: boolean;
  captureException?: CaptureExceptionFunction;
};

export const useGetUser = (config: UseGetUserConfig): UseGetUserResponse => {
  const { userId, skip, captureException } = config;

  const {
    data,
    loading,
    error,
    refetch: apolloRefetch,
  } = useUsersPageGetUserQuery({
    variables: { id: userId },
    skip: skip || !userId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const refetch = useCallback(async () => {
    try {
      await apolloRefetch();
    } catch (refetchError) {
      captureException?.(refetchError as Error);
      throw refetchError;
    }
  }, [apolloRefetch, captureException]);

  const user = data?.user ? mapToUser(data.user) : null;

  return {
    user,
    isLoading: loading,
    error: error || null,
    refetch,
  };
};
